<script>
  import MultipleDateSelector from './multiple_date_selector.svelte'
  import Combobox from './ui/combobox.svelte'

  // Bindable value matches app/models/schedule.rb exactly (see README "Schedule value shape"):
  // { recur_on, time_zone, starts_at, ends_at, starts_on, ends_on, sunday..saturday,
  //   schedule_exclusions_attributes: [{date}], schedule_inclusions_attributes: [{date}] }
  // schedule_(exclusions|inclusions)_attributes use replace_attributes_for server-side (full
  // replace on save). Plain { date } rows: AdminSite::ScheduleParams matches dates to the
  // saved rows server-side, so a resave keeps their ids.
  let { value = $bindable(defaultSchedule()), timeZones = [], error } = $props()

  const DAYS = [
    ['sunday', 'Sun'],
    ['monday', 'Mon'],
    ['tuesday', 'Tue'],
    ['wednesday', 'Wed'],
    ['thursday', 'Thu'],
    ['friday', 'Fri'],
    ['saturday', 'Sat']
  ]

  function defaultSchedule() {
    return {
      recur_on: 'weekly',
      time_zone: 'America/New_York',
      starts_at: null,
      ends_at: null,
      starts_on: null,
      ends_on: null,
      sunday: true,
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: true,
      schedule_exclusions_attributes: [],
      schedule_inclusions_attributes: []
    }
  }

  // Every change replaces the whole value: the bound parent is often a `useForm` store, whose
  // plain objects don't react to in-place property writes.
  const set = (key, next) => (value = { ...value, [key]: next })

  const exclusionDates = {
    get value() {
      return value.schedule_exclusions_attributes.map((attrs) => attrs.date)
    },
    set value(dates) {
      set('schedule_exclusions_attributes', dates.map((date) => ({ date })))
    }
  }

  const inclusionDates = {
    get value() {
      return value.schedule_inclusions_attributes.map((attrs) => attrs.date)
    },
    set value(dates) {
      set('schedule_inclusions_attributes', dates.map((date) => ({ date })))
    }
  }
</script>

<div class="flex flex-col gap-4 min-w-0">
  <fieldset class="fieldset">
    <legend class="fieldset-legend">Recurrence</legend>
    <Combobox
      bind:value={() => value.recur_on, (next) => set('recur_on', next)}
      size="sm"
      width="w-full max-w-xs"
      ariaLabel="Recurrence"
      options={[
        { value: 'weekly', label: 'Weekly (recurring days)' },
        { value: 'specific_days', label: 'Specific dates only' },
        { value: 'range', label: 'Date range' }
      ]}
    />
  </fieldset>

  {#if value.recur_on === 'weekly'}
    <fieldset class="fieldset">
      <legend class="fieldset-legend">Days of week</legend>
      <div class="flex flex-wrap gap-1">
        {#each DAYS as [key, short] (key)}
          <button
            type="button"
            class="btn btn-sm"
            class:btn-primary={value[key]}
            onclick={() => set(key, !value[key])}
          >
            {short}
          </button>
        {/each}
      </div>
    </fieldset>
  {/if}

  <!-- Rows wrap: two editors side by side (quests) would otherwise overflow into each other. -->
  <div class="flex flex-wrap gap-x-4">
    <fieldset class="fieldset">
      <legend class="fieldset-legend">Starts at</legend>
      <input type="time" class="input input-sm w-40" bind:value={() => value.starts_at, (next) => set('starts_at', next)} />
    </fieldset>
    <fieldset class="fieldset">
      <legend class="fieldset-legend">Ends at</legend>
      <input type="time" class="input input-sm w-40" bind:value={() => value.ends_at, (next) => set('ends_at', next)} />
    </fieldset>
    <fieldset class="fieldset">
      <legend class="fieldset-legend">Time zone</legend>
      {#if timeZones.length > 0}
        <Combobox bind:value={() => value.time_zone, (next) => set('time_zone', next)} size="sm" width="w-56" ariaLabel="Time zone" options={timeZones} />
      {:else}
        <input type="text" class="input input-sm" bind:value={() => value.time_zone, (next) => set('time_zone', next)} />
      {/if}
    </fieldset>
  </div>

  {#if value.recur_on === 'range'}
    <div class="flex flex-wrap gap-x-4">
      <fieldset class="fieldset">
        <legend class="fieldset-legend">Starts on</legend>
        <input type="date" class="input input-sm" bind:value={() => value.starts_on, (next) => set('starts_on', next)} />
      </fieldset>
      <fieldset class="fieldset">
        <legend class="fieldset-legend">Ends on</legend>
        <input type="date" class="input input-sm" bind:value={() => value.ends_on, (next) => set('ends_on', next)} />
      </fieldset>
    </div>
  {/if}

  {#if value.recur_on === 'weekly'}
    <MultipleDateSelector bind:value={exclusionDates.value} label="Exclude these dates" />
  {/if}

  {#if value.recur_on === 'specific_days'}
    <MultipleDateSelector bind:value={inclusionDates.value} label="Include these dates" />
  {/if}

  {#if error}<p class="text-error text-sm">{Array.isArray(error) ? error[0] : error}</p>{/if}
</div>
